'use client'

/* eslint-disable @next/next/no-img-element -- official network logo asset
   files from public/icons/social (real brand marks, colors baked into the
   SVG files), same pattern as the press-logo band. */

/**
 * ShareBar — THE share control for every Fridge.com page family (PDPs,
 * collections, hubs, tools, articles, press, local, rebates, filters,
 * compare, recalls, data figures and sections). Owner spec 07/20:
 *
 *  - REAL official brand logos from asset files (public/icons/social/*.svg —
 *    the official network marks with official colors baked in), never
 *    hand-drawn approximations.
 *  - FULL icons — no padding chips, no borders, no circles around them. The
 *    logo itself is the button.
 *  - ALWAYS colorful at rest. Hover scales slightly; color is never hidden
 *    behind a hover state.
 *  - ONE LINE always: the icon row never wraps at any width, including 375.
 *    Icons are ~22px with tight gaps; the optional label yields (truncates)
 *    first so the icons stay on a single line. Mount containers are laid out
 *    so the row always has the width it needs — zero clipping.
 *  - Interactive affordance on every icon: pointer cursor, aria-label,
 *    visible focus ring.
 *
 * Renders X, Facebook, LinkedIn, Pinterest, Reddit, WhatsApp, Telegram, email,
 * copy-link, and the native Web Share sheet (shown only once mounted, since
 * navigator.share is client-only). Used both per-page and per-figure.
 *
 * Every outbound share URL is built at click time from the explicit page or
 * live location, on the canonical Fridge.com origin with its section anchor.
 * Every link is tagged with UTM params
 * (utm_source=<network>, utm_medium=social, utm_campaign=<family>,
 * utm_content=<key>) so the citation/share channel is measurable. Pinterest
 * uses a native link instead of a scripted popup. Explicit page targets work
 * before hydration; location-derived targets wait for hydration. The native-share button is gated behind a
 * mounted flag.
 *
 * Light styling only, Fridge.com palette, works at every width.
 */
import { useEffect, useState } from 'react'
import { Check, Share2 } from 'lucide-react'
import { Link2Icon, MailIcon } from '@/components/brand-kit/icon-sprite'
import { useIsHydrated } from '@/hooks/use-is-hydrated'
import { canonicalShareUrl, trackedShareUrl, pinterestShareUrl } from '@/lib/social-share-url'
import { trackPinterestSaveInitiated } from '@/lib/pinterest-save-analytics'

interface ShareBarProps {
  /** Title used in share copy (X/email/WhatsApp/etc). */
  title: string
  /** Canonical page URL (SSR fallback + share target base). */
  pageUrl?: string
  /** Optional section anchor appended to the shared URL. */
  anchorId?: string
  /** utm_content value — which figure/section/page is being shared. */
  utmContent: string
  /** utm_campaign value — which page family the share came from. Defaults to 'data'. */
  utmCampaign?: string
  /** Optional image URL for Pinterest pin (the branded share card). */
  imageUrl?: string
  /** 'row' = icons grow to 24px from sm up; 'compact' = fixed 22px. */
  variant?: 'row' | 'compact'
  /** Optional leading label (e.g. "Share this product"). Hidden when unset. */
  label?: string
  /**
   * WHAT is being shared, for every button's title/aria-label (owner
   * directive FRIDGE-352, 08/17/2026: "absolutly all share button meta
   * headers (when you highlight) should say what and with Fridge.com brand
   * in it"). A short noun phrase — "the LG LRFXS2503S", "this rebate", "the
   * Price Index by Brand". Defaults to `title`, so every existing caller is
   * already covered; pass it when the share copy is not a good name.
   */
  subject?: string
  className?: string
}

/** The brand exactly once: appended unless the phrase already carries it. */
function withBrandOnce(phrase: string): string {
  return /fridge\.com/i.test(phrase) ? phrase : `${phrase} — Fridge.com`
}

/**
 * The tooltip / accessible name of one share control: what is being shared,
 * where it goes, and the Fridge.com brand — e.g. "Share the LG LRFXS2503S on
 * X — Fridge.com", "Copy the Fridge.com link to this rebate". Exported so the
 * PDP image share sheet and the sitewide guard test build the exact same
 * strings.
 */
export function shareControlMeta(subject: string, action: 'network' | 'email' | 'copy' | 'native', network?: string): string {
  const what = subject.trim().replace(/\s*[—|-]\s*Fridge\.com\s*$/i, '').trim() || 'this page'
  switch (action) {
    case 'network':
      return withBrandOnce(`Share ${what} on ${network ?? 'social media'}`)
    case 'email':
      return withBrandOnce(`Share ${what} by email`)
    case 'copy':
      return /fridge\.com/i.test(what) ? `Copy the link to ${what}` : `Copy the Fridge.com link to ${what}`
    case 'native':
      return withBrandOnce(`More ways to share ${what}`)
  }
}

function toast(msg: string) {
  if (typeof window === 'undefined') return
  const el = document.createElement('div')
  el.textContent = msg
  el.className =
    'fixed bottom-4 right-4 bg-gray-900 text-white text-[13px] font-bold px-4 py-2 rounded-full shadow-lg z-50'
  document.body.appendChild(el)
  setTimeout(() => {
    if (document.body.contains(el)) document.body.removeChild(el)
  }, 1800)
}

// Real official logo asset files — public/icons/social/{network}.svg, official
// marks with the official brand color baked into each file. Exported so the PDP
// image share sheet (components/product/ProductImageShareSheet.tsx) renders
// the very same official marks — one icon system sitewide (owner spec 07/20).
export const SOCIAL_ICON_SRC: Record<string, string> = {
  x: '/icons/social/x.svg',
  facebook: '/icons/social/facebook.svg',
  linkedin: '/icons/social/linkedin.svg',
  pinterest: '/icons/social/pinterest.svg',
  reddit: '/icons/social/reddit.svg',
  whatsapp: '/icons/social/whatsapp.svg',
  telegram: '/icons/social/telegram.svg',
  instagram: '/icons/social/instagram.svg',
}
const ICON_SRC = SOCIAL_ICON_SRC

// Fridge.com brand orange — the resting ink for email / copy / native share.
const BRAND_ORANGE = '#ff9951'

export function ShareBar({
  title,
  pageUrl,
  anchorId,
  utmContent,
  utmCampaign = 'data',
  imageUrl,
  variant = 'row',
  label,
  subject,
  className = '',
}: ShareBarProps) {
  const [copied, setCopied] = useState(false)
  const mounted = useIsHydrated()

  // What every tooltip / accessible name says is being shared (FRIDGE-352).
  const what = (subject ?? title).trim()

  // Resolve at click time, honoring explicit targets and canonicalizing previews.
  const bareUrl = () => canonicalShareUrl(pageUrl, typeof window === 'undefined' ? undefined : window.location.href, anchorId)
  const target = (utmSource: string) => trackedShareUrl(bareUrl(), utmSource, utmCampaign, utmContent)

  const openWin = (u: string) =>
    window.open(u, '_blank', 'noopener,noreferrer,width=620,height=560')

  // Every shared payload must carry the Fridge.com brand — but exactly once.
  // Callers may or may not already include it in `title`, so append only when
  // it isn't there (guards against "… — Fridge.com — Fridge.com").
  const text = /fridge\.com\s*$/i.test(title.trim()) ? title.trim() : `${title.trim()} — Fridge.com`

  // Icon sizing: 22px at 375 keeps the full 10-icon row + label on ONE line
  // (10×22px icons + 9×4px gaps = 256px inside a ~335px content column). The
  // 'row' variant grows to 24px from sm up.
  const iconCls =
    variant === 'compact' ? 'w-[22px] h-[22px]' : 'w-[22px] h-[22px] sm:w-6 sm:h-6'

  // The logo IS the button — no chip, no border, no background (owner spec).
  // Pointer cursor, slight scale on hover (color stays on), visible focus ring.
  const btn =
    'social-icon-control'

  const nets: Array<{ key: string; label: string; go: () => void }> = [
    { key: 'x', label: 'X', go: () => openWin(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(target('x'))}`) },
    { key: 'facebook', label: 'Facebook', go: () => openWin(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(target('facebook'))}`) },
    { key: 'linkedin', label: 'LinkedIn', go: () => openWin(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(target('linkedin'))}`) },
    { key: 'pinterest', label: 'Pinterest', go: () => openWin(pinterestShareUrl(target('pinterest'), text, imageUrl)) },
    { key: 'reddit', label: 'Reddit', go: () => openWin(`https://www.reddit.com/submit?url=${encodeURIComponent(target('reddit'))}&title=${encodeURIComponent(text)}`) },
    { key: 'whatsapp', label: 'WhatsApp', go: () => openWin(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${text} ${target('whatsapp')}`)}`) },
    { key: 'telegram', label: 'Telegram', go: () => openWin(`https://t.me/share/url?url=${encodeURIComponent(target('telegram'))}&text=${encodeURIComponent(text)}`) },
  ]

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(bareUrl())
      setCopied(true)
      toast('Link copied')
      setTimeout(() => setCopied(false), 1600)
    } catch {
      toast('Copy failed')
    }
  }

  const emailShare = () => {
    // `text` already carries the Fridge.com brand exactly once (deduped above).
    window.location.href = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`I thought you'd find this useful:\n\n${text}\n${target('email')}`)}`
  }

  const nativeShare = async () => {
    try {
      await (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }).share?.({
        title,
        text,
        url: target('native'),
      })
    } catch {
      /* user cancelled */
    }
  }

  const canNative = mounted && typeof navigator !== 'undefined' && 'share' in navigator
  // Native links preserve keyboard, mobile and open-in-new-tab behavior even
  // when scripted popups are unavailable. Do not invent a homepage target while
  // a location-derived share is still server rendering.
  const pinterestHref = pageUrl || mounted
    ? pinterestShareUrl(target('pinterest'), text, imageUrl)
    : undefined
  const pinterestHasMedia = !!pinterestHref && new URL(pinterestHref).searchParams.has('media')

  // Layout (owner spec 07/20): the ICON ROW is always ONE line — icons never
  // wrap mid-row, never drop, never shrink below legible. When the full text
  // label + icons cannot share one line (mobile), the row stacks as exactly
  // two lines: line 1 the label, line 2 the full icon row. The outer
  // container wraps; the icon group itself never does.
  // Every control's title + aria-label says WHAT is shared and carries the
  // brand (FRIDGE-352): "Share the LG LRFXS2503S on X — Fridge.com",
  // "Copy the Fridge.com link to this rebate". Built by shareControlMeta so
  // the PDP share sheet and the guard test produce identical strings.
  const emailMeta = shareControlMeta(what, 'email')
  const copyMeta = copied ? withBrandOnce(`Fridge.com link copied — ${what}`) : shareControlMeta(what, 'copy')
  const nativeMeta = shareControlMeta(what, 'native')

  return (
    <div
      className={`flex flex-wrap items-center gap-x-1.5 gap-y-1.5 ${className}`}
      role="group"
      aria-label={withBrandOnce(`Share ${what.replace(/\s*[—|-]\s*Fridge\.com\s*$/i, '') || 'this page'}`)}
    >
      {label && (
        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#697080] mr-0.5 whitespace-nowrap shrink-0">
          {label}
        </span>
      )}
      <span className="flex flex-nowrap items-center gap-1 sm:gap-1.5">
        {nets.map((n) => {
          // Without media Pinterest chooses a page preview, not this SVG figure.
          const meta = shareControlMeta(n.key === 'pinterest' && !pinterestHasMedia ? `the page for ${what}` : what, 'network', n.label)
          if (n.key === 'pinterest' && pinterestHref) {
            return (
              <a key={n.key} href={pinterestHref} target="_blank" rel="noopener noreferrer"
                onClick={(event) => {
                  const destination = target('pinterest')
                  event.currentTarget.href = pinterestShareUrl(destination, text, imageUrl)
                  trackPinterestSaveInitiated(destination, utmCampaign, new URL(event.currentTarget.href).searchParams.has('media'))
                }}
                className={btn} title={meta} aria-label={meta}>
                <img src={ICON_SRC[n.key]} alt="" width={22} height={22} className={iconCls} loading="lazy" decoding="async" />
              </a>
            )
          }
          return (
            <button key={n.key} type="button" onClick={n.go} className={btn} title={meta} aria-label={meta}>
              <img src={ICON_SRC[n.key]} alt="" width={22} height={22} className={iconCls} loading="lazy" decoding="async" />
            </button>
          )
        })}
        <button type="button" onClick={emailShare} className={btn} title={emailMeta} aria-label={emailMeta}>
          <MailIcon className={iconCls} style={{ color: BRAND_ORANGE }} />
        </button>
        <button type="button" onClick={copy} className={btn} title={copyMeta} aria-label={copyMeta}>
          {copied ? (
            <Check className={`${iconCls} text-[#0469f0]`} />
          ) : (
            <Link2Icon className={iconCls} style={{ color: BRAND_ORANGE }} />
          )}
        </button>
        {canNative && (
          <button type="button" onClick={nativeShare} className={btn} title={nativeMeta} aria-label={nativeMeta}>
            <Share2 className={iconCls} style={{ color: BRAND_ORANGE }} />
          </button>
        )}
      </span>
    </div>
  )
}
